/**
 * assets/wishlist-supabase.js
 * ---------------------------------------------------------------------
 * Server-side, variant-aware wishlist synced to a Supabase Postgres
 * instance. No localStorage, no app-store dependency.
 *
 * REQUIRED HOST MARKUP (add once in layout/theme.liquid, before </body>):
 *
 *   <script
 *     id="WishlistSupabase"
 *     type="module"
 *     src="{{ 'wishlist-supabase.js' | asset_url }}"
 *     data-supabase-url="{{ settings.supabase_url }}"
 *     data-supabase-anon-key="{{ settings.supabase_anon_key }}"
 *     data-customer-id="{{ customer.id }}"
 *   ></script>
 *
 * `settings.supabase_url` / `settings.supabase_anon_key` should be theme
 * settings (Theme Editor > Theme settings) or hardcoded constants — never
 * the Supabase service_role key, only the public anon key.
 *
 * Minimal CSS to pair with this (add to base.css or a wishlist.css file):
 *
 *   .wishlist-button__icon { transition: transform .15s ease; }
 *   .wishlist-button.is-active .wishlist-button__icon {
 *     fill: currentColor;
 *     transform: scale(1.08);
 *   }
 */

import { createClient } from 'https://esm.sh/@supabase/supabase-js@2';

const CONFIG_EL = document.getElementById('WishlistSupabase');

if (!CONFIG_EL) {
  console.warn('[Wishlist] #WishlistSupabase config script tag not found. Wishlist disabled.');
} else {
  const SUPABASE_URL = CONFIG_EL.dataset.supabaseUrl;
  const SUPABASE_ANON_KEY = CONFIG_EL.dataset.supabaseAnonKey;
  const CUSTOMER_ID = CONFIG_EL.dataset.customerId || null;
  const REST_ENDPOINT = `${SUPABASE_URL}/rest/v1/wishlists`;
  const BUTTON_SELECTOR = '[data-wishlist-button]';

  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    global: {
      headers: CUSTOMER_ID ? { 'x-customer-id': CUSTOMER_ID } : {}
    }
  });

  const state = {
    variantIds: new Set()
  };

  function restHeaders() {
    return {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      'x-customer-id': CUSTOMER_ID || ''
    };
  }

  // ---------------------------------------------------------------------
  // 1. Init: hydrate heart state for every wishlist button on the page
  // ---------------------------------------------------------------------
  function paintButton(button) {
    const variantId = button.dataset.variantId;
    const active = state.variantIds.has(variantId);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  }

  function paintAllButtons() {
    document.querySelectorAll(BUTTON_SELECTOR).forEach(paintButton);
  }

  async function hydrateWishlistState() {
    if (!CUSTOMER_ID) return; // guest — buttons already render as login links

    const { data, error } = await supabase
      .from('wishlists')
      .select('variant_id')
      .eq('customer_id', CUSTOMER_ID);

    if (error) {
      console.error('[Wishlist] Failed to hydrate wishlist state:', error.message);
      return;
    }

    state.variantIds = new Set((data || []).map((row) => row.variant_id));
    paintAllButtons();
  }

  // ---------------------------------------------------------------------
  // 2. Product page: keep the PDP wishlist button's variant id current
  // ---------------------------------------------------------------------
  function resolveActiveVariantId(scopeEl) {
    // Dawn's product-form keeps the authoritative selected variant in a
    // hidden <input name="id"> that it rewrites on every variant change.
    const hiddenInput = scopeEl.querySelector('input[name="id"]');
    return hiddenInput ? hiddenInput.value : null;
  }

  function bindVariantWatcher() {
    const pickers = document.querySelectorAll('variant-selects, variant-radios');

    pickers.forEach((picker) => {
      picker.addEventListener('change', () => {
        // Dawn rewrites the hidden variant input asynchronously after its
        // own 'change' handler runs, so defer a frame before reading it.
        requestAnimationFrame(() => {
          const form =
            picker.closest('.product-form') ||
            document.querySelector('form[action*="/cart/add"]');

          if (!form) return;

          const variantId = resolveActiveVariantId(form);
          const productButton = document.querySelector(
            `${BUTTON_SELECTOR}[data-context="product"]`
          );

          if (variantId && productButton) {
            productButton.dataset.variantId = variantId;
            paintButton(productButton);
          }
        });
      });
    });
  }

  // ---------------------------------------------------------------------
  // 3. Click: optimistic toggle + direct REST POST/DELETE against Supabase
  // ---------------------------------------------------------------------
  async function addToWishlist(handle, variantId) {
    const response = await fetch(REST_ENDPOINT, {
      method: 'POST',
      headers: {
        ...restHeaders(),
        // merge-duplicates makes this call idempotent against the
        // (customer_id, variant_id) unique index — safe to double-click.
        Prefer: 'resolution=merge-duplicates,return=minimal'
      },
      body: JSON.stringify({
        customer_id: CUSTOMER_ID,
        product_handle: handle,
        variant_id: variantId
      })
    });

    if (!response.ok) {
      throw new Error(`Wishlist insert failed: ${response.status}`);
    }
    state.variantIds.add(variantId);
  }

  async function removeFromWishlist(variantId) {
    const query = new URLSearchParams({
      customer_id: `eq.${CUSTOMER_ID}`,
      variant_id: `eq.${variantId}`
    });

    const response = await fetch(`${REST_ENDPOINT}?${query.toString()}`, {
      method: 'DELETE',
      headers: { ...restHeaders(), Prefer: 'return=minimal' }
    });

    if (!response.ok) {
      throw new Error(`Wishlist delete failed: ${response.status}`);
    }
    state.variantIds.delete(variantId);
  }

  async function handleClick(event) {
  let button = event.target.closest(BUTTON_SELECTOR);

  /*
   * Dawn collection cards use a stretched .full-unstyled-link layer.
   * That layer may physically receive the click even when the user
   * clicked the visible wishlist button.
   *
   * If the normal closest() lookup fails, detect whether the click
   * coordinates are inside a wishlist button.
   */
  if (!button) {
    const buttons = document.querySelectorAll(BUTTON_SELECTOR);

    for (const candidate of buttons) {
      const rect = candidate.getBoundingClientRect();

      const clickedInsideButton =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (clickedInsideButton) {
        button = candidate;
        break;
      }
    }
  }

  if (!button || button.tagName !== 'BUTTON') {
    return;
  }

  console.log('[Wishlist] HEART CLICK DETECTED', {
    button,
    target: event.target,
    handle: button.dataset.handle,
    variantId: button.dataset.variantId
  });

  /*
   * VERY IMPORTANT:
   * Stop Dawn's stretched product link from opening the product page.
   */
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  if (!CUSTOMER_ID) {
    window.location.href = '/account/register';
    return;
  }

  const handle = button.dataset.handle;
  const variantId = button.dataset.variantId;

  if (!handle || !variantId) {
    console.warn(
      '[Wishlist] Button is missing handle/variant data.',
      button
    );

    return;
  }

  const willBeActive = !button.classList.contains('is-active');

  // Optimistic UI
  button.classList.toggle('is-active', willBeActive);
  button.setAttribute('aria-pressed', String(willBeActive));
  button.disabled = true;

  try {
    if (willBeActive) {
      await addToWishlist(handle, variantId);
    } else {
      await removeFromWishlist(variantId);
    }

    /*
     * Repaint every heart for the same variant.
     * Useful if the same product appears multiple times on the page.
     */
    document
      .querySelectorAll(
        `${BUTTON_SELECTOR}[data-variant-id="${CSS.escape(variantId)}"]`
      )
      .forEach((wishlistButton) => {
        wishlistButton.classList.toggle(
          'is-active',
          willBeActive
        );

        wishlistButton.setAttribute(
          'aria-pressed',
          String(willBeActive)
        );
      });

    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        detail: {
          handle,
          variantId,
          active: willBeActive
        }
      })
    );

    console.log('[Wishlist] SYNC SUCCESS', {
      handle,
      variantId,
      active: willBeActive
    });
  } catch (error) {
    console.error(
      '[Wishlist] Sync failed, reverting button state.',
      error
    );

    button.classList.toggle(
      'is-active',
      !willBeActive
    );

    button.setAttribute(
      'aria-pressed',
      String(!willBeActive)
    );
  } finally {
    button.disabled = false;
  }
}

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------
  function init() {
  console.log('[Wishlist] Initializing wishlist');

  document.addEventListener(
    'click',
    handleClick,
    true
  );

  bindVariantWatcher();
  hydrateWishlistState();

  console.log('[Wishlist] Wishlist READY');
}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
